'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { useStore } from '@/store'
import { useCustomerSegmentsQuery } from '@/generated/graphql'
import { getAdvertisingModalOpenState } from '@/AdvertisingModal/advertisingModalState'
import { safeLocalStorage } from '@/AdvertisingModal/utils/errorHandling'
import { DailyDealsSignupModal } from './DailyDealsSignupModal'
import {
  DAILY_DEALS_GUEST_SUBSCRIBED_KEY,
  DAILY_DEALS_POPUP_DELAY_MS,
  dailyDealsPopupEligible,
  dailyDealsPopupAllowedOnPath,
} from './dailyDealsPopupPolicy'

/** Campaign scheduling only; form, modal and tracking use the shared advertising components. */
export function DailyDealsPopup({
  active,
  campaign,
}: {
  active: boolean
  campaign: string
}) {
  const pathname = usePathname()
  const enabled = active && dailyDealsPopupAllowedOnPath(pathname)
  const ready = useStore(state => state.hydrated && state.authResolved)
  const userId = useStore(state => state.user?.id ?? null)
  const [openViewer, setOpenViewer] = useState<string | null>(null)
  const viewer = userId == null ? 'guest' : `customer:${userId}`
  const segments = useCustomerSegmentsQuery(
    {},
    {
      queryKey: ['CustomerSegments', 'daily-deals', userId],
      enabled: enabled && ready && userId != null,
      staleTime: 60_000,
    }
  )
  const membershipKnown =
    segments.isSuccess && segments.data?.customerSegments != null
  const subscribed = Boolean(
    segments.data?.customerSegments?.segments?.some(
      segment => segment?.id === 20
    )
  )
  const storageKey = `dailyDealsPopupShown:${campaign}`
  useEffect(() => {
    const eligible = () =>
      dailyDealsPopupEligible({
        active: enabled,
        ready,
        signedIn: userId != null,
        membershipKnown,
        subscribed,
        guestSubscribed:
          safeLocalStorage.getItem(DAILY_DEALS_GUEST_SUBSCRIBED_KEY) === 'true',
        lastShown: Number(safeLocalStorage.getItem(storageKey)) || 0,
        now: Date.now(),
      })
    if (!eligible()) return
    const timer = setTimeout(() => {
      // Recheck browser state at display time, including changes in another tab.
      if (
        document.visibilityState !== 'visible' ||
        getAdvertisingModalOpenState() ||
        !eligible()
      )
        return
      // If storage is unavailable, suppress automatic prompts rather than repeating them.
      if (!safeLocalStorage.setItem(storageKey, String(Date.now()))) return
      setOpenViewer(viewer)
    }, DAILY_DEALS_POPUP_DELAY_MS)
    return () => clearTimeout(timer)
  }, [enabled, ready, userId, membershipKnown, subscribed, storageKey, viewer])

  const visible =
    openViewer === viewer &&
    enabled &&
    ready &&
    (userId == null || (membershipKnown && !subscribed))
  return (
    <DailyDealsSignupModal
      isOpen={visible}
      onClose={() => setOpenViewer(null)}
    />
  )
}
